import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { useFileUpload } from '../../../../utils/hooks/useFileUpload';
import { useNotes } from '../../../../utils/hooks/useNotes';
import { motion } from 'motion/react';
import { transition, variants } from './variants';
import API from '../../../../API';
import styles from './Location.module.css';

export default function Location() {

    const { qr_data, dataDispatch } = useAuth();
    const { deleteSnapshot } = API;
    const { notes, setNotes } = useNotes(qr_data?.id);

    const snapshot = useMemo(() => qr_data?.data?.location_snapshot || '', [qr_data?.data?.location_snapshot]);

    const [ loading, setLoading ] = useState(false);
    const [ remove, setRemove ] = useState(false);
    const [ showModal, setShowModal ] = useState(false);
    const [ dragActive, setDragActive ] = useState(false);

    const { handleFileSelected } = useFileUpload({
        acceptableFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
        fileHandleType: 'location',
    });

    const handleUpload = async (e) => {
        setLoading(true);
        try {
            await handleFileSelected(e.target.files);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        };
    };

    const handleDeleteSnapshot = async () => {
        setLoading(true);
        try {
            await deleteSnapshot(qr_data?.id);
            dataDispatch({type: 'location_snapshot', data: null});
        } catch (err) {
            console.error('Failed to delete snapshot.', err);
        } finally {
            setLoading(false);
            setRemove(false);
        };
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setLoading(true);
            try {
                await handleFileSelected(e.dataTransfer.files);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        let timer;
        if (remove) {
            timer = setTimeout(() => {
                setRemove(false);
            },3000);
        };

        return () => timer && clearTimeout(timer);
    }, [remove]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setShowModal(false);
        };
        if (showModal) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    return (
        <div className={styles.container}>
            <h5 className={styles.header}>Location</h5>
            {snapshot && (
                <div className={styles.snapWrap}>
                    <div 
                        className={styles.snap} 
                        onClick={() => setShowModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowModal(true);
                            }
                        }}
                    >
                        <div className={styles.thumbnailContainer}>
                            <img src={`${snapshot}?${Date.now()}`} alt='Location of QR' className={styles.thumbnail} />
                        </div>
                        <span className={styles.hoverText}>Click to View Full Size</span>
                    </div>
                    <div className={styles.deleteWrap}>
                        <motion.button
                            key='remove/cancel'
                            className={styles.deleteBtn}
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={!remove ? 'initial1' : 'animate1'}
                            onClick={() => setRemove(!remove)}
                        >
                            {!remove ? <><i className='far fa-trash-can' /> Remove Snapshot</> : <><i className='far fa-xmark' />Cancel</>}
                        </motion.button>
                        <motion.button
                            key='delete'
                            className={styles.confirmBtn}
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={remove ? 'animate2' : 'initial2'}
                            onClick={handleDeleteSnapshot}
                            disabled={loading}
                        >
                            <i className='far fa-trash-can' />
                            Confirm
                        </motion.button>
                    </div>
                </div>
            )}
            {!snapshot && (
                <>
                <div className={styles.words}>
                    Want to remember where you placed your QR sticker? Upload a photo of it. 
                    This image will be saved to your account, so you'll always have a visual reminder of the sticker's exact location.
                </div>
                <input type='file' id={`snap-upload-${qr_data?.id}`} className={styles.input} accept='image/png,image/jpeg,image/jpg,image/webp' onChange={handleUpload} disabled={loading} />
                <label 
                    htmlFor={`snap-upload-${qr_data?.id}`} 
                    className={`${styles.inputBtn} ${dragActive ? styles.dragActive : ''}`}
                    title='Upload'
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    {!loading ? (
                        <>
                        <i className='far fa-upload' />
                        <span>{dragActive ? 'Drop to upload' : 'Click or drag to upload'}</span>
                        <span className={styles.fileTypes}>(png, jpeg, jpg, webp)</span>
                        </>
                        )
                        : <i className='fas fa-spinner fa-spin' />
                    }
                </label>
                </>
            )}

            <h5 className={styles.header} style={{ marginTop: '2rem' }}>Notes</h5>
            <textarea 
                id='location-notes-textarea' 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                className={styles.textarea}
                placeholder="Add notes about this QR code location..."
            />

            {showModal && (
                <motion.div
                    className={styles.modalOverlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setShowModal(false)}
                >
                    <motion.div
                        className={styles.modalContent}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            className={styles.modalClose}
                            onClick={() => setShowModal(false)}
                            aria-label="Close modal"
                        >
                            <i className="fas fa-times" />
                        </button>
                        <img 
                            src={`${snapshot}?${Date.now()}`} 
                            alt='Location of QR - Full Size' 
                            className={styles.modalImage}
                        />
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};
